import type { DepartmentDto, MeResponse } from '@process-hub/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { describeError } from '../errors';

/** Names the process and its department, then opens the interview canvas. */
export function InterviewStart({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<DepartmentDto[] | null>(null);
  const [departmentId, setDepartmentId] = useState('');
  const [name, setName] = useState('');
  const [maintainerRole, setMaintainerRole] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .listDepartments()
      .then((d) => {
        setDepartments(d);
        if (d.length === 1) setDepartmentId(d[0].id);
      })
      .catch((e: unknown) => setError(describeError(e)));
  }, []);

  const canStart = me.isAdmin || me.roles.includes('ProcessOwner');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const started = await api.startInterview({ departmentId, name: name.trim(), maintainerRole: maintainerRole.trim() });
      // The File rides along in history state (structured-clone safe); the canvas uploads it on arrival (D-030).
      navigate(`/interview/${started.sessionId}`, { state: file ? { file } : undefined });
    } catch (err) {
      setError(describeError(err, 'The interview could not be started.'));
      setBusy(false);
    }
  }

  if (!canStart) return <div className="notice">Only process owners start interviews. Ask your department head if you should own a process.</div>;
  if (error && !departments) return <div className="notice notice--error">{error}</div>;
  if (!departments) return <div className="notice">Loading departments.</div>;

  return (
    <form className="card form" onSubmit={submit}>
      <div className="card__header">
        <div>
          <div className="card__title">Start an interview</div>
          <div className="grid__secondary">Twenty to forty minutes. Describe what actually happens; you review every step afterwards.</div>
        </div>
      </div>
      <div className="form__body">
        <label className="field">
          <span>Process name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Quote to Order" minLength={3} maxLength={200} required autoFocus />
        </label>
        <label className="field">
          <span>Department</span>
          <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} required>
            <option value="" disabled>
              Choose a department
            </option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Job title that maintains this process</span>
          <input value={maintainerRole} onChange={(e) => setMaintainerRole(e.target.value)} placeholder="Sales Operations Manager" minLength={2} maxLength={120} required />
          <small className="muted">A role, not a person — the process outlives its owner.</small>
        </label>
        <label className="field">
          <span>Existing procedure document or Scribe export (optional)</span>
          <input type="file" accept=".pdf,.docx,.html,.htm,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/html,text/plain,text/markdown" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <small className="muted">
            {file ? `${file.name} — the interviewer drafts the process from it and asks only about what it leaves unclear.` : 'PDF, Word, HTML, text or Markdown. Scribe exports work here too — export the Scribe as PDF, HTML, Word or Markdown and attach it; its screenshots come along and are placed on the steps. If you have an SOP already, attach it and the interview gets shorter.'}
          </small>
        </label>
        {error && <div className="notice notice--error">{error}</div>}
        <div className="form__actions">
          <button type="submit" className="button" disabled={busy || !departmentId}>
            {busy ? 'Starting…' : 'Begin the interview'}
          </button>
        </div>
      </div>
    </form>
  );
}
